/**
 * useChannelList — v.1-D §7.12, Decision D-007.
 *
 * Merges two reactive Convex subscriptions into the single denormalised view
 * the UI renders:
 *   - `api.channels.listChannels`                — static channel rows.
 *   - `api.channelState.getRunningChannelsSnapshot` — per-running-channel
 *     `ChannelState` (`running` | `ad_break`).
 *
 * The merged `ChannelView` carries BOTH vocabularies side-by-side:
 *   - `status`        — mockup-domain `MockupChannelStatus` (`live`/`adbreak`/
 *                       `idle`/`starting`/`stopping`/`error`).
 *   - `backendStatus` — backend `ChannelState` (`idle`/`starting`/`running`/
 *                       `ad_break`/`stopping`/`error`). Carried so analytics /
 *                       audit components don't have to round-trip through the
 *                       mockup-domain name.
 *
 * Loading: returns `undefined` until BOTH subscriptions have resolved. D-007:
 * surfacing a half-merged view would flicker channels between "idle" and
 * "live" on every snapshot tick.
 */
import { useMemo } from 'react';
import { useQuery } from 'convex/react';
import { api } from '@convex/api';
import { channelThumbGradient } from '../lib/utils';
import {
  toMockupStatus,
  toDestinationsArray,
  type ChannelState,
  type MockupChannelStatus,
} from '../lib/adapters';

export interface ChannelView {
  id:            string;
  name:          string;
  slug:          string;
  status:        MockupChannelStatus;
  backendStatus: ChannelState;
  edge:          string;
  destinations:  readonly string[];
  thumb:         string;
}

interface ChannelRow {
  _id: string;
  name: string;
  slug?: string | undefined;
  edgeProvider?: string;
  outputDestination?: 'youtube_live' | 'o_and_o' | 'both' | undefined;
}

interface RunningSnapshotEntry {
  channelId: string;
  status: ChannelState;
}

export function useChannelList(): ChannelView[] | undefined {
  const channels = useQuery(api.channels.listChannels) as
    | ChannelRow[]
    | undefined;
  const liveStates = useQuery(api.channelState.getRunningChannelsSnapshot) as
    | RunningSnapshotEntry[]
    | undefined;

  const statusByChannelId = useMemo<Map<string, ChannelState> | null>(() => {
    if (!liveStates) return null;
    const m = new Map<string, ChannelState>();
    for (const s of liveStates) m.set(s.channelId, s.status);
    return m;
  }, [liveStates]);

  return useMemo(() => {
    if (channels === undefined || statusByChannelId === null) return undefined;
    return channels.map<ChannelView>((c) => {
      const backendStatus: ChannelState =
        statusByChannelId.get(c._id) ?? 'idle';
      return {
        id:            c._id,
        name:          c.name,
        slug:          c.slug
          ? c.slug.toUpperCase()
          : c._id.slice(-8).toUpperCase(),
        status:        toMockupStatus(backendStatus),
        backendStatus,
        edge:          c.edgeProvider ?? 'Unknown',
        destinations:  toDestinationsArray(c.outputDestination),
        thumb:         channelThumbGradient(c._id),
      };
    });
  }, [channels, statusByChannelId]);
}
